import { useEffect } from 'react';
import { api, apiGet, ApiError } from '@/api/client';

const KEY = 'suite.ingreso';

/** Avisa a las demás pestañas de este navegador que aquí se acaba de ingresar (`storage` solo llega a las otras). */
export function avisarIngreso() {
  try { localStorage.setItem(KEY, String(Date.now())); } catch { /* modo privado */ }
}

/**
 * Varias pestañas de una app vuelven a ingresar a la vez (venció su sesión, o un back-channel): cada una llega a la
 * portada con su interacción en `volver`. Cuando la persona ingresa en una, las demás siguen solas al volver a verlas
 * (o al enterarse por `storage`) si su interacción ya se puede terminar con la sesión nueva; así se ingresa una vez
 * (GUIA §3.1, «Varias pestañas»). Si la interacción venció, igual se sigue cuando hay sesión: la interacción reabre la
 * app. Sin sesión, la portada se queda como está.
 */
export function useSigueSiOtraPestanaIngreso(volver: string | null) {
  useEffect(() => {
    const uid = volver?.match(/^\/interaccion\/([^/?#]+)/)?.[1];
    if (!uid) return;
    let yendo = false;
    const revisar = async () => {
      if (yendo || document.visibilityState !== 'visible') return;
      try {
        const s = await api<{ loggedIn: boolean }>(`/${uid}/state`, { base: '/oidc/interaction' });
        if (!s.loggedIn) return;
      } catch (e) {
        if (!(e instanceof ApiError && e.code === 'INTERACTION_INVALID')) return;
        if (!(await apiGet('/me').then(() => true, () => false))) return;
      }
      yendo = true;
      window.location.replace(volver!);
    };
    const onStorage = (e: StorageEvent) => { if (e.key === KEY) void revisar(); };
    const onVisible = () => void revisar();
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [volver]);
}
